/**
 * customer-error-logger.js
 *
 * Companion to the inline error-capture bootstrap in theme.liquid.
 * On load: drains the buffer populated by the bootstrap, then takes over
 * as the live `window.__errCapture` function for any future errors.
 */
(function () {
  'use strict';

  // --- Config ---------------------------------------------------------------
  const WEBHOOK_URL = 'https://hook.us2.make.com/ze9jlocqqdgw4vvrhf7j8mdomfc617jf';
  const MAX_ERRORS_PER_SESSION = 25;
  const MAX_FIELD_LENGTH = 2000;
  const DEDUP_WINDOW_MS = 3000;

  // --- State ----------------------------------------------------------------
  let errorCount = 0;
  let isSending = false;
  const recentSignatures = new Map();

  // --- Helpers --------------------------------------------------------------
  function clip(value, max) {
    if (value == null) return '';
    const str = typeof value === 'string' ? value : String(value);
    return str.length > max ? str.slice(0, max) + '…[truncated]' : str;
  }

  function getCustomer() {
    const data = window.ShopifyData || {};
    if (!data.customerId) return null;
    return {
      id: data.customerId,
      email: data.customerEmail || null,
      warehouse: data.customerWarehouse || null
    };
  }

  function isDesignMode() {
    const data = window.ShopifyData || {};
    return data.designMode || (typeof Shopify !== 'undefined' && Shopify.designMode);
  }

  function isDuplicate(payload) {
    const sig = `${payload.error.type}|${payload.error.message}|${payload.error.line}`;
    const now = Date.now();
    const last = recentSignatures.get(sig);
    if (last && now - last < DEDUP_WINDOW_MS) return true;
    recentSignatures.set(sig, now);
    if (recentSignatures.size > 50) {
      const oldest = recentSignatures.keys().next().value;
      recentSignatures.delete(oldest);
    }
    return false;
  }

  // --- Core send ------------------------------------------------------------
  function send(errorInfo) {
    if (isSending) return;
    if (isDesignMode()) return;

    const customer = getCustomer();
    if (!customer) return;

    if (errorCount >= MAX_ERRORS_PER_SESSION) return;

    isSending = true;
    try {
      const data = window.ShopifyData || {};
      const payload = {
        timestamp: new Date().toISOString(),
        shop: data.shop,
        customer: customer,
        error: {
          type: errorInfo.type || 'error',
          message: clip(errorInfo.message, MAX_FIELD_LENGTH),
          source: clip(errorInfo.source, 500),
          line: errorInfo.line || null,
          column: errorInfo.column || null,
          stack: clip(errorInfo.stack, MAX_FIELD_LENGTH)
        },
        context: {
          url: window.location.href,
          referrer: document.referrer || null,
          userAgent: navigator.userAgent,
          viewport: { w: window.innerWidth, h: window.innerHeight },
          market: data.marketWarehouse || null,
          extra: errorInfo.context || null
        }
      };

      if (isDuplicate(payload)) return;
      errorCount++;

      fetch(WEBHOOK_URL, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        credentials: 'omit'
      }).catch(function () { /* silent */ });
    } catch (_) {
      /* silent */
    } finally {
      setTimeout(function () { isSending = false; }, 0);
    }
  }

  // --- Drain buffered errors from the inline bootstrap ----------------------
  if (Array.isArray(window.__errBuffer)) {
    window.__errBuffer.forEach(send);
    window.__errBuffer.length = 0;
  }

  // --- Replace the buffer-push with the real send ---------------------------
  window.__errCapture = send;

  // --- Public API for manual reports ----------------------------------------
  window.logCustomerError = function (err, context) {
    send({
      type: 'manual',
      message: (err && err.message) ? err.message : String(err),
      stack: err && err.stack,
      context: context
    });
  };
})();